import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { stores } from 'mobx-store';
import { MatchDTO } from '../../DTO';
import AddEditMatchModal from './MatchForm';
import moment from 'moment';
import { useHistory } from 'react-router';

const MatchesScreen: React.FC = observer(() => {
  const history = useHistory();
  const { liveScoringStore } = stores;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<MatchDTO | null>(null);
  const [selectedTournamentId, setSelectedTournamentId] = useState<number | ''>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [matchToDelete, setMatchToDelete] = useState<MatchDTO | null>(null);

  useEffect(() => {
    const init = async () => {
      await liveScoringStore.getTournaments();
      liveScoringStore.getSports();
      liveScoringStore.getGenders();
      liveScoringStore.getTeams();
    };
    init();
  }, [liveScoringStore]);

  useEffect(() => {
    if (selectedTournamentId) {
      liveScoringStore.getMatches(selectedTournamentId);
    }
  }, [selectedTournamentId, liveScoringStore]);

  const handleOpenAdd = () => {
    setSelectedMatch(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (match: MatchDTO) => {
    setSelectedMatch(match);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (selectedTournamentId) {
      liveScoringStore.getMatches(selectedTournamentId);
    }
  };

  const handleOpenDeleteDialog = (match: MatchDTO) => {
    setMatchToDelete(match);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setMatchToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (matchToDelete) {
      await liveScoringStore.deleteMatch(matchToDelete.id);
      handleCloseDeleteDialog();
    }
  };

  return (
    <Container>
      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Matches Management
        </Typography>
        <Button variant="contained" color="primary" onClick={handleOpenAdd}>
          Add New Match
        </Button>
      </Box>

      <Box mb={3} mt={2}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel>Tournament</InputLabel>
          <Select
            value={selectedTournamentId}
            onChange={(e) => setSelectedTournamentId(e.target.value as number)}
            label="Tournament"
          >
            <MenuItem value="">
              <em>Select Tournament</em>
            </MenuItem>
            {liveScoringStore.tournaments.map((tournament) => (
              <MenuItem key={tournament.id} value={tournament.id}>
                {tournament.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} style={{ marginTop: 20 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Home</TableCell>
              <TableCell>Away</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Tournament</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {liveScoringStore.matches.map((match) => (
              <TableRow key={match.id}>
                <TableCell>{match.id}</TableCell>
                <TableCell>{match.teamH.name}</TableCell>
                <TableCell>{match.teamA?.name || 'N/A'}</TableCell>
                <TableCell>{match.gender?.title || 'N/A'}</TableCell>
                <TableCell>
                  {match.start_date
                    ? moment(match.start_date).format('MMMM Do YYYY, h:mm:ss a')
                    : ''}
                </TableCell>
                <TableCell>{match.tournament?.name}</TableCell>

                <TableCell align="right">
                  <Button
                    style={{ margin: 10 }}
                    variant="outlined"
                    color="primary"
                    onClick={() => handleOpenEdit(match)}
                  >
                    Edit
                  </Button>
                  <Button
                    style={{ margin: 5 }}
                    variant="outlined"
                    color="primary"
                    onClick={() => history.push(`matches/${match.id}/events`)}
                  >
                    Events
                  </Button>
                  <IconButton
                    style={{ marginLeft: 5 }}
                    color="secondary"
                    onClick={() => handleOpenDeleteDialog(match)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {selectedTournamentId === '' && (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ padding: 20 }}>
                  Please select a tournament to view matches.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddEditMatchModal
        open={isModalOpen}
        onClose={handleCloseModal}
        match={selectedMatch}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {matchToDelete?.teamH.name} vs {matchToDelete?.teamA?.name || 'N/A'}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">Cancel</Button>
          <Button onClick={handleConfirmDelete} color="secondary">Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
});

export default MatchesScreen;
